import { supabase } from "@/integrations/supabase/client";
import { OvertimeStatus } from "@/types/enums";

/**
 * Calculate the total overtime hours for an employee in a specific month
 * @param employeeId - The employee ID to calculate hours for
 * @param date - The date (YYYY-MM-DD) to determine the month
 * @param includeCurrentRequest - Whether to include the current request hours
 * @param currentRequestHours - Hours in the current request (if including)
 * @returns Total overtime hours for the month
 */
export async function calculateMonthlyOvertimeHours(
  employeeId: string,
  date: string,
  includeCurrentRequest = false,
  currentRequestHours = 0
): Promise<number> {
  try {
    // Extract the year and month from the date
    const [year, month] = date.split('-');
    const startDate = `${year}-${month}-01`;
    
    // Calculate the end date (last day of the month)
    const nextMonth = parseInt(month) === 12 ? '01' : String(parseInt(month) + 1).padStart(2, '0');
    const nextYear = parseInt(month) === 12 ? String(parseInt(year) + 1) : year;
    const endDate = `${nextYear}-${nextMonth}-01`;
    
    // Query only approved overtime requests for this employee in the specified month
    const { data } = await supabase
      .from('overtime_requests')
      .select('hours')
      .eq('employee_id', employeeId)
      .eq('status', OvertimeStatus.APPROVED) // Only count approved requests
      .gte('date', startDate)
      .lt('date', endDate);
    
    // Calculate the total approved hours
    let totalHours = data?.reduce((sum, request) => sum + parseFloat(String((request as any).hours)), 0) || 0;
    
    // Add current request hours if needed
    if (includeCurrentRequest) {
      totalHours += currentRequestHours;
    }
    
    return totalHours;
    
  } catch (error) {
    console.error('Error calculating monthly overtime hours:', error);
    return 0; // Return 0 as a fallback
  }
}

/**
 * Get the company's maximum overtime hours threshold
 * @param companyId - The company ID to get the threshold for
 * @returns The maximum overtime hours threshold
 */
export async function getCompanyOvertimeThreshold(companyId: string): Promise<number> {
  try {
    // First try to get from the specific company
    const { data: companyData } = await supabase
      .from('companies')
      .select('max_overtime_hours')
      .eq('id', companyId)
      .eq('is_deleted', false)
      .single();
    
    if (companyData && 'max_overtime_hours' in companyData && (companyData as any).max_overtime_hours !== undefined && (companyData as any).max_overtime_hours !== null) {
      return (companyData as any).max_overtime_hours;
    }
    
    // If no specific company threshold, get the default from any active company
    const { data: defaultData } = await supabase
      .from('companies')
      .select('max_overtime_hours')
      .eq('is_deleted', false)
      .limit(1)
      .single();
    
    if (defaultData && 'max_overtime_hours' in defaultData && (defaultData as any).max_overtime_hours !== undefined && (defaultData as any).max_overtime_hours !== null) {
      return (defaultData as any).max_overtime_hours;
    }
    
    return 104; // Default threshold as fallback
  } catch (error) {
    console.error('Error fetching overtime threshold:', error);
    return 104; // Default threshold as fallback
  }
}

/**
 * Check if total overtime hours exceed the company threshold
 * @param totalHours - The total overtime hours
 * @param threshold - The company's threshold
 * @returns Boolean indicating if VP approval is required
 */
export function isVpApprovalRequired(totalHours: number, threshold: number): boolean {
  return totalHours > threshold;
}


